// ─────────────────────────────────────────────────────────────────────────────
// Painel de gerência — a parte que é só conta.
//
// O que este módulo responde é a pergunta do gerente do suporte: "o que está
// acontecendo AGORA?". Não é relatório, não é histórico, não é avaliação de
// ninguém — é o quadro de plantão que se olha de relance para decidir a próxima
// ação dos próximos vinte minutos.
//
// Aqui não há fetch nem hook, de propósito: as regras que erram na prática são
// as de classificação (o que conta como vencido, o que conta como carga alta,
// quem está "sem registro"), e elas precisam poder ser conferidas sem subir a
// aplicação. Ver scripts/teste-gerencia.mjs.
//
// Quem busca os dados é services/gerenciaApi.js; quem os põe na tela é
// pages/views/Gerencia.js.
// ─────────────────────────────────────────────────────────────────────────────

// Com extensão: este arquivo também é importado pelo `node` nos testes.
import { compararPrioridade } from './prioridade.js';

// ── Constantes repetidas de propósito ────────────────────────────────────────
//
// Os números abaixo já existem em services/atividadeApi.js (o limite do café, o
// da ociosidade) e em services/mysuiteApi.js (os códigos de status). Eles são
// repetidos aqui pelo mesmo motivo que mysuiteApi.js repete os códigos de
// codigosMysuite.js: para este arquivo continuar SEM DEPENDÊNCIA NENHUMA e,
// portanto, conferível sob `node` — atividadeApi.js importa o cliente Supabase,
// e importá-lo aqui arrastaria a rede para dentro do teste.
//
// Se um deles mudar lá, muda aqui. O teste cobre o comportamento, não o valor.

/** Minutos sem atender até o Orion registrar ociosidade. Espelha INATIVIDADE_LIMITE_MIN. */
export const OCIOSIDADE_LIMITE_MIN = 25;
/** Teto do café quando o operador não tem valor próprio cadastrado. */
export const CAFE_LIMITE_PADRAO_MIN = 15;
/** Folga padrão antes de um desvio virar excesso. */
export const TOLERANCIA_PADRAO_MIN = 5;

/** Fila genérica do suporte: ticket aqui não tem dono de fato. */
export const OPERADOR_SEM_DONO = 106;

/**
 * As filas do suporte que o painel mostra.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DUAS ORIGENS, PORQUE UMA SÓ NÃO ENXERGA TUDO
 *
 * A fila de pendentes (`/dashboard/suporte/ticket/pendentes`) devolve os
 * status 1, 5, 36, 90 e 104 — com `codigoOperador` e `dataAbertura`, que é o
 * formato mais completo. Mas ela NÃO devolve o 18 (Setor de Sistemas), e
 * nunca devolveria: conferido contra a API, ele simplesmente não está lá.
 *
 * `POST /tickets/filtro` com `codigoStatus` e SEM operador devolve a fila
 * geral daquele status, o 18 incluído — e ele não é pequeno: 81 tickets em
 * 31/08/2026, o dobro da fila de pendentes inteira. Deixá-lo de fora seria
 * esconder a maior fila do setor.
 *
 * O preço é que os dois formatos diferem (ver `normalizarTicket`) e que as
 * duas leituras DIVERGEM entre si: no mesmo instante, o filtro trouxe cinco
 * tickets no status 5 que os pendentes não tinham, e os pendentes trouxeram
 * três que o filtro não tinha. Por isso as duas são unidas, e não escolhidas
 * — é o mesmo raciocínio de `operadorEstaAtendendo` em mysuiteApi.js.
 * ─────────────────────────────────────────────────────────────────────────
 */
// A `cor` de cada fila é um slot da paleta do design system (Orion.css), e não
// um hex novo: são os mesmos tons já validados para daltonismo na tela de
// Atividade. Cada fila tem o seu, para o olho separar as listas sem ler o
// rótulo — que é o que se pede de uma tela consultada dezenas de vezes por dia.
// `apuraSla: false` tira a fila da apuração de atraso — ver `situacaoDoSla`.
// `soAnaliseSuporte: true` restringe a fila ao operador 106 — ver `pertenceAFila`.
export const STATUS = {
  1: { chave: 'aguardando', curto: 'Aguard.', rotulo: 'Aguardando solução', sub: 'Análise Suporte', origem: 'pendentes', cor: 'recebida', apuraSla: true, soAnaliseSuporte: true },
  5: { chave: 'retorno', curto: 'Retorno', rotulo: 'Fila de retorno', origem: 'ambas', cor: 'aguard', apuraSla: true },
  18: { chave: 'sistemas', curto: 'Sistemas', rotulo: 'Setor de Sistemas', origem: 'filtro', cor: 'alt', apuraSla: false },
  36: { chave: 'atendimento', curto: 'Atend.', rotulo: 'Em atendimento', origem: 'pendentes', cor: 'atend', apuraSla: true },
  90: { chave: 'validacao', curto: 'Validação', rotulo: 'Aguardando validação do cliente', origem: 'pendentes', cor: 'cus', apuraSla: true },
  104: { chave: 'agendado', curto: 'Agend.', rotulo: 'Agendado', origem: 'pendentes', cor: 'mel', apuraSla: true },
};

/**
 * Este ticket entra na apuração de atraso?
 *
 * ─────────────────────────────────────────────────────────────────────────
 * O SETOR DE SISTEMAS NÃO ENTRA — E O NÚMERO MOSTRA POR QUÊ
 *
 * Ticket encaminhado ao Setor de Sistemas saiu das mãos do suporte: o prazo
 * de lá é outro, e cobrar o analista por ele é cobrar por trabalho que não é
 * dele. Foi a regra que o Jonathan estabeleceu em 31/08/2026.
 *
 * O efeito é grande, e é o que torna a régua confiável. Antes desta regra, o
 * bloco "Fora do SLA" mostrava doze tickets — e os SETE primeiros eram do
 * Sistemas, estourados havia de 53 a 101 dias. Eles empurravam para baixo os
 * atrasos de horas, que são os que alguém ainda pode resolver hoje.
 *
 * A fila do Sistemas continua inteira na tela, com o total, a cor e a lista.
 * O que ela não faz é contar como atraso do suporte.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function apuraSla(ticket) {
  const st = STATUS[Number(ticket?.codigoStatusTicket)];
  // Status desconhecido apura, de propósito: só quem é explicitamente marcado
  // como fora da régua fica fora dela. Um status novo que aparecesse na API
  // seria medido normalmente, e não sumiria da apuração sem ninguém notar.
  return st ? st.apuraSla !== false : true;
}

/**
 * A fila que abre por padrão: "Aguardando solução" — a Análise Suporte.
 *
 * É a fila de entrada do setor, onde cai o que ainda não foi tratado, e por
 * isso é a que responde à pergunta que se faz ao abrir a tela: "o que chegou e
 * está esperando?". As outras são consultadas quando se quer olhar para elas;
 * esta é o estado normal da tela.
 */
export const FILA_PADRAO = 1;

/**
 * Este ticket pertence à fila daquele status, do ponto de vista do SETOR?
 *
 * ─────────────────────────────────────────────────────────────────────────
 * "AGUARDANDO SOLUÇÃO" É A FILA DE ENTRADA, E NÃO O STATUS
 *
 * O status 1 tem duas populações que não são a mesma coisa:
 *
 *   · ticket com o operador ANÁLISE SUPORTE (106) — chegou e não foi
 *     tratado. É a fila de entrada do setor, o que se quer ver ao abrir a
 *     tela: trabalho esperando alguém;
 *   · ticket em status 1 com DONO — alguém já o pegou e ele está no fluxo
 *     daquela pessoa. Não está esperando ninguém.
 *
 * Misturar os dois inflava a fila de entrada com trabalho que já tem
 * responsável, e a fila de entrada é o número que decide se alguém precisa
 * puxar mais um agora. Regra estabelecida pelo Jonathan em 31/08/2026.
 *
 * A função é o ÚNICO lugar que decide isso, e vale para o contador da ficha
 * e para a lista — se as duas divergissem, a ficha diria "3" e abriria
 * quatro, e o painel perderia a credibilidade inteira.
 *
 * CONSEQUÊNCIA ASSUMIDA: um ticket em status 1 COM dono não aparece em
 * nenhuma ficha de fila. Ele continua contado na coluna "Aguard." do quadro
 * de equipe, no nome de quem o tem — que é onde ele deve ser cobrado.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function pertenceAFila(ticket, codigoStatus) {
  const cod = Number(codigoStatus);
  if (Number(ticket?.codigoStatusTicket) !== cod) return false;
  const regra = STATUS[cod];
  if (!regra?.soAnaliseSuporte) return true;
  return donoDoTicket(ticket) === OPERADOR_SEM_DONO;
}

/** Status que só existem em `/tickets/filtro` — precisam de consulta própria. */
export const STATUS_SO_NO_FILTRO = Object.entries(STATUS)
  .filter(([, s]) => s.origem === 'filtro')
  .map(([codigo]) => Number(codigo));

// ── Normalização das duas origens ────────────────────────────────────────────

/**
 * O código de status a partir do TEXTO, para o que vem de `/tickets/filtro`.
 *
 * Aquele endpoint devolve `status` como frase ("SUP - Em Análise / Sistemas") e
 * não devolve `codigoStatusTicket`. Como cada consulta é feita POR status, o
 * chamador já sabe qual pediu — este mapa é a reserva para quando a resposta
 * misturar (o filtro por operador, por exemplo, traz vários status juntos).
 */
export function codigoDoStatusTexto(texto) {
  const t = String(texto || '');
  if (/em\s+an[áa]lise\s*\/\s*sistemas/i.test(t)) return 18;
  if (/em\s+atendimento/i.test(t)) return 36;
  if (/fila\s+para\s+retorno/i.test(t)) return 5;
  if (/aguardando\s+valida/i.test(t)) return 90;
  if (/^agendado/i.test(t)) return 104;
  if (/aguardando\s+solu/i.test(t)) return 1;
  return null;
}

/**
 * O código da criticidade a partir do TEXTO, casando pela DESCRIÇÃO.
 *
 * ATENÇÃO — o número que aparece na frente do texto NÃO é o código.
 * Conferido contra a API e contra orion.criticidade em 31/08/2026:
 *
 *   texto "4- Urgente: Precisa cumprir prazos de Fechamentos"  → cri_codigo 9
 *   texto "2-Alto: Afeta um setor ou processo essencial"       → cri_codigo 4
 *   texto "7- Programada: Necessita Agendamento"               → cri_codigo 11
 *
 * O prefixo é uma numeração de EXIBIÇÃO do MySuite, de outra escala. Usá-lo
 * como código colocaria o ticket na faixa errada de gravidade — que é
 * exatamente o tipo de erro que ninguém nota até alguém agir por ele.
 *
 * Por isso o casamento é pela descrição, que é o que as duas pontas têm em
 * comum: o trecho depois do primeiro ':'.
 */
export function codigoDaCriticidadeTexto(texto, porDescricao = {}) {
  const bruto = String(texto || '').trim();
  if (!bruto) return null;
  const depoisDoDoisPontos = bruto.includes(':') ? bruto.slice(bruto.indexOf(':') + 1) : bruto;
  const chave = normalizarTexto(depoisDoDoisPontos);
  return porDescricao[chave] ?? null;
}

/** Minúsculas, sem acento, sem espaço sobrando. Para casar descrições. */
export function normalizarTexto(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim().toLowerCase().replace(/\s+/g, ' ');
}

/** `{ [descrição normalizada]: código }`, a partir de orion.criticidade. */
export function indicePorDescricao(criticidades = {}) {
  const indice = {};
  Object.entries(criticidades).forEach(([codigo, descricao]) => {
    indice[normalizarTexto(descricao)] = Number(codigo);
  });
  return indice;
}

/**
 * Põe um ticket no formato único do painel, venha ele de onde vier.
 *
 * ACRESCENTA os campos canônicos e PRESERVA todos os originais, de propósito:
 * o Resumo do Ticket e `assumirTicket.js` leem os campos crus (`operador`,
 * `status`), e removê-los quebraria os dois. O que este normalizador garante é
 * que `codigoOperador` e `codigoStatusTicket` existam SEMPRE — porque é por
 * eles que o painel agrupa, filtra e conta.
 *
 * `statusPedido` é o status da consulta que trouxe este ticket; ele vence,
 * porque é a informação mais confiável que existe sobre a resposta.
 */
export function normalizarTicket(bruto, { statusPedido = null, porDescricao = {} } = {}) {
  if (!bruto) return null;

  const dono = Number(bruto.codigoOperador ?? bruto.operador);
  const status = Number(bruto.codigoStatusTicket)
    || statusPedido
    || codigoDoStatusTexto(bruto.status);
  const criticidade = Number.isFinite(Number(bruto.codigoCriticidade))
    && Number(bruto.codigoCriticidade) !== 0
    ? Number(bruto.codigoCriticidade)
    : codigoDaCriticidadeTexto(bruto.criticidade, porDescricao);

  return {
    ...bruto,
    codigo: Number(bruto.codigo),
    codigoOperador: Number.isFinite(dono) ? dono : null,
    codigoStatusTicket: Number.isFinite(status) ? status : null,
    codigoCriticidade: Number.isFinite(criticidade) ? criticidade : null,
  };
}

/**
 * Une as duas origens sem duplicar.
 *
 * O mesmo ticket pode vir das duas (o status 5 vem), e nesse caso vence o
 * registro MAIS COMPLETO — o da fila de pendentes, que traz `dataAbertura` e os
 * códigos crus. A ordem dos argumentos decide: o primeiro tem precedência.
 */
export function unirFilas(...listas) {
  const porCodigo = new Map();
  listas.forEach((lista) => {
    (lista || []).forEach((t) => {
      if (!t || t.codigo == null) return;
      if (!porCodigo.has(t.codigo)) porCodigo.set(t.codigo, t);
    });
  });
  return [...porCodigo.values()];
}

/**
 * Este ticket pode ser puxado por quem está olhando?
 *
 * A regra é a MESMA de `decidirAssumir` (services/assumirTicket.js), e de
 * propósito: o botão só aparece onde a ação de fato passaria. Um "Puxar" que
 * abre um aviso de recusa é pior do que botão nenhum.
 *
 * Só é puxável o ticket SEM DONO DE FATO — o que está na fila genérica (106) ou
 * sem operador nenhum. Ticket de colega nunca aparece com botão: tirá-lo da
 * fila de alguém sem que a pessoa saiba é o que o Orion não faz.
 */
export function podePuxar(ticket, meuCodigo) {
  // `Number(null)` é 0, e 0 passa em `isFinite` — sem esta guarda, o operador
  // SEM cadastro em orion.operador veria o botão e o clique falharia depois, no
  // MySuite. O código 0 também não existe: é o "manter o atual" do MySuite
  // (ver OPERADOR.MANTER_ATUAL em codigosMysuite.js).
  if (meuCodigo == null) return false;
  const meu = Number(meuCodigo);
  if (!Number.isFinite(meu) || meu === 0) return false;
  const dono = donoDoTicket(ticket);
  if (dono === meu) return false;                       // já é seu
  if (Number(ticket?.codigoStatusTicket) === 36) return false;  // já está em atendimento
  return dono == null || dono === OPERADOR_SEM_DONO;
}

/** Rótulo de um status pelo código. O código cru quando for um que não conhecemos. */
export function rotuloDoStatus(codigo) {
  return STATUS[codigo]?.rotulo || `Status ${codigo}`;
}

/** Como cada estado de atividade se chama e se apresenta no quadro. */
export const ESTADOS = {
  ativo: { rotulo: 'Ativo', tom: 'ok', icone: 'activity' },
  almoco: { rotulo: 'Almoço', tom: 'neutro', icone: 'coffee' },
  cafe: { rotulo: 'Café', tom: 'neutro', icone: 'coffee' },
  apoio: { rotulo: 'Apoio a colega', tom: 'neutro', icone: 'users' },
  ociosidade: { rotulo: 'Ociosidade', tom: 'alerta', icone: 'clock' },
  fora_expediente: { rotulo: 'Fora do expediente', tom: 'apagado', icone: 'moon' },
  reuniao: { rotulo: 'Reunião', tom: 'neutro', icone: 'users' },
  outro: { rotulo: 'Outro', tom: 'neutro', icone: 'pen' },
  encerrado: { rotulo: 'Sem período aberto', tom: 'apagado', icone: 'clock' },
  ausente: { rotulo: 'Sem registro hoje', tom: 'alerta', icone: 'alert-triangle' },
  // Tem ticket na fila do suporte mas não está na escala de plantão — quase
  // sempre alguém que mudou de setor e ficou com tickets no nome.
  externo: { rotulo: 'Fora do plantão', tom: 'apagado', icone: 'user' },
  // A fila genérica. Não é pessoa, mas ocupa uma linha do quadro.
  fila: { rotulo: 'Fila genérica', tom: 'alerta', icone: 'inbox' },
};

// ── Datas ────────────────────────────────────────────────────────────────────

/** Minutos decorridos entre dois instantes. Nunca negativo. */
export function minutosEntre(inicio, fim) {
  if (!inicio || !fim) return null;
  return Math.max(0, Math.round((fim.getTime() - inicio.getTime()) / 60000));
}

/**
 * Duração em texto curto — "48 min", "1h08". O mesmo formato da pílula de
 * atividade, para o gerente e o operador lerem a mesma coisa do mesmo jeito.
 */
export function formatarDuracao(min) {
  if (!Number.isFinite(min)) return '—';
  const m = Math.max(0, Math.round(min));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/** Dias corridos desde uma data "YYYY-MM-DD". null quando a data não presta. */
export function diasDesde(dataTexto, agora = new Date()) {
  const bruta = String(dataTexto || '').trim();
  if (!bruta || bruta.startsWith('0000')) return null;
  const m = bruta.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((agora.getTime() - d.getTime()) / 86400000);
}

/** "há 3 dias", "há 2 meses". Para a idade de um ticket parado. */
export function textoDeDias(dias) {
  if (!Number.isFinite(dias)) return '—';
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  if (dias < 365) return `há ${Math.floor(dias / 30)} meses`;
  const anos = Math.floor(dias / 365);
  return anos === 1 ? 'há mais de 1 ano' : `há mais de ${anos} anos`;
}

// ── Estado de cada operador ──────────────────────────────────────────────────

/**
 * O previsto de uma pausa, em minutos, para o tipo informado.
 *
 * Espelha `referenciaPausa` de atividadeApi.js — ver a nota das constantes lá
 * em cima. Devolve `null` para os tipos que NÃO têm tempo de referência:
 * apoio a colega, reunião e "outro" não têm duração combinada, e inventar uma
 * transformaria o painel num cronômetro em cima de quem está ajudando alguém.
 */
export function previstoDaPausa(expediente, tipo) {
  if (!expediente) return null;

  if (tipo === 'almoco') {
    const { almocoIni, almocoFim } = expediente;
    if (almocoIni == null || almocoFim == null) return null;
    return { previsto: almocoFim - almocoIni, folga: expediente.tolerancia ?? TOLERANCIA_PADRAO_MIN };
  }

  // Café e ociosidade não têm tolerância: o teto já É o combinado, e dar cinco
  // minutos de folga num café de quinze seria um terço da pausa inteira.
  if (tipo === 'cafe') {
    return { previsto: expediente.cafeLimite ?? CAFE_LIMITE_PADRAO_MIN, folga: 0 };
  }
  if (tipo === 'ociosidade') {
    return { previsto: OCIOSIDADE_LIMITE_MIN, folga: 0 };
  }

  return null;
}

/**
 * O estado de um operador AGORA, a partir do período em aberto.
 *
 * `periodo` é a linha de orion.operador_atividade com `fim is null`, ou null
 * quando não há nenhuma. `temRegistroHoje` distingue as duas ausências, que são
 * situações bem diferentes para quem coordena:
 *
 *   · SEM registro nenhum no dia  → a pessoa não abriu o Orion. É o dado que
 *     revela uma falta antes de alguém dar por ela;
 *   · COM registro e sem período aberto → trabalhou e encerrou (ou o período
 *     foi fechado no fim da jornada). Não é alarme.
 *
 * `excedeu` só é verdade quando a pausa TEM tempo de referência e o passou.
 */
export function estadoDoOperador(periodo, { temRegistroHoje = false, expediente = null, agora = new Date() } = {}) {
  if (!periodo) {
    const chave = temRegistroHoje ? 'encerrado' : 'ausente';
    return { chave, ...ESTADOS[chave], minutos: null, excedeu: false, excesso: null };
  }

  const chave = periodo.status === 'ativo' ? 'ativo' : (periodo.tipo || 'outro');
  const base = ESTADOS[chave] || ESTADOS.outro;
  // `periodo.inicio` já marca o começo real da inatividade — a ociosidade é
  // gravada desde ali, não desde o alerta de 25 min —, então isto já É o
  // tempo decorrido de verdade. Somar o limite aqui contaria os mesmos
  // minutos duas vezes, o que já foi o erro do aviso de retorno do operador.
  const decorrido = minutosEntre(periodo.inicio, agora);

  const ref = periodo.status === 'inativo' ? previstoDaPausa(expediente, chave) : null;
  const excesso = ref && Number.isFinite(decorrido) ? decorrido - ref.previsto : null;
  const excedeu = !!(ref && excesso != null && excesso > ref.folga);

  return {
    chave,
    rotulo: base.rotulo,
    tom: excedeu ? 'alerta' : base.tom,
    icone: base.icone,
    minutos: decorrido,
    excedeu,
    excesso: excedeu ? excesso : null,
    previsto: ref ? ref.previsto : null,
  };
}

// ── Fila ─────────────────────────────────────────────────────────────────────

/**
 * O código do dono de um ticket.
 *
 * Lê os DOIS nomes de campo, como `donoDoTicket` de assumirTicket.js já fazia:
 * a fila de pendentes responde `codigoOperador` e `/tickets/filtro` responde
 * `operador`. Aceitar só um deles deixaria metade da fila sem dono aparente.
 */
export function donoDoTicket(ticket) {
  const n = Number(ticket?.codigoOperador ?? ticket?.operador);
  return Number.isFinite(n) ? n : null;
}

/** Como a fila genérica do suporte se chama na tela. */
export const NOME_FILA_GENERICA = 'Análise Suporte (sem dono)';

/**
 * O NOME de quem está com o ticket. A única fonte disso na tela.
 *
 * Existe para que "Operador 104" nunca mais apareça por descuido. A ordem de
 * resolução é:
 *
 *   1. a fila genérica (106) tem nome próprio — ela não é pessoa, e chamá-la
 *      de "Operador 106" faria parecer que existe alguém com esse nome;
 *   2. sem dono nenhum, "sem dono" — que é a informação, e não uma falha;
 *   3. o mapa de nomes, que cobre TODO operador cadastrado (ver
 *      `carregarNomes`, e note que ele lê da view, onde o nome já pode vir de
 *      space.usuarios);
 *   4. só então, o código cru. É o último recurso, e agora só acontece se o
 *      MySuite devolver um operador que não existe no cadastro do Orion.
 *
 * `meuCodigo`, quando informado, faz a própria pessoa aparecer como "você" —
 * o que ela procura primeiro numa fila de cem linhas.
 */
export function nomeDoOperador(codigo, { nomes = {}, meuCodigo = null } = {}) {
  const cod = Number(codigo);
  if (cod === OPERADOR_SEM_DONO) return NOME_FILA_GENERICA;
  if (!Number.isFinite(cod) || codigo == null) return 'sem dono';
  if (meuCodigo != null && cod === Number(meuCodigo)) return 'você';
  return nomes[cod] || `Operador ${cod}`;
}

/**
 * Agrupa a fila por operador: `{ [codigo]: { total, porStatus, tickets } }`.
 *
 * A fila de pendentes já vem com o dono em cada linha, então UMA resposta da
 * API resolve o time inteiro — não são treze consultas, uma por pessoa.
 */
export function agruparPorOperador(tickets) {
  const mapa = {};
  (tickets || []).forEach((t) => {
    const dono = donoDoTicket(t);
    if (dono == null) return;
    if (!mapa[dono]) mapa[dono] = { total: 0, porStatus: {}, tickets: [] };
    const st = Number(t.codigoStatusTicket);
    mapa[dono].total += 1;
    mapa[dono].porStatus[st] = (mapa[dono].porStatus[st] || 0) + 1;
    mapa[dono].tickets.push(t);
  });
  return mapa;
}

/**
 * A carga média por operador ATIVO na fila.
 *
 * Conta só quem tem ticket, e ignora a fila genérica: incluir os zeros e o 106
 * puxaria a média para baixo e faria todo mundo parecer sobrecarregado.
 */
export function cargaMedia(porOperador) {
  const cargas = Object.entries(porOperador || {})
    .filter(([cod]) => Number(cod) !== OPERADOR_SEM_DONO)
    .map(([, v]) => v.total);
  if (!cargas.length) return 0;
  return cargas.reduce((a, b) => a + b, 0) / cargas.length;
}

/**
 * Está sobrecarregado? Duas condições, e as duas precisam valer.
 *
 * O múltiplo sozinho não serve: num dia fraco, dois tickets contra uma média de
 * 0,8 dispararia o alerta sem que ninguém estivesse afogado. O piso absoluto
 * evita esse falso positivo.
 */
export function estaSobrecarregado(total, media, { multiplo = 1.8, piso = 5 } = {}) {
  return total >= piso && media > 0 && total >= media * multiplo;
}

// ── Prazos ───────────────────────────────────────────────────────────────────

/**
 * O prazo de um ticket como Date, aceitando as variações de formato da API.
 *
 * Mesma leitura de `prazoDoTicket` em filaRetorno.js — repetida aqui porque
 * aquele módulo trata da fila de UM operador, com as regras de aviso, e este
 * trata da fila de TODOS. Manter os dois independentes evita que uma mudança na
 * régua de aviso do operador mexa, sem querer, no painel do gerente.
 */
export function prazoDoTicket(ticket) {
  if (!ticket) return null;
  const bruta = String(ticket.dataPrevisao || ticket.data_previsao || '').trim();
  if (!bruta || bruta.startsWith('0000')) return null;

  let ano;
  let mes;
  let dia;
  const iso = bruta.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const br = bruta.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (iso) { [, ano, mes, dia] = iso; }
  else if (br) { [, dia, mes, ano] = br; }
  else return null;

  const hora = String(ticket.horaPrevisao || ticket.hora_previsao || '').trim();
  const hm = hora.match(/^(\d{1,2}):(\d{2})/);
  const d = new Date(Number(ano), Number(mes) - 1, Number(dia), hm ? Number(hm[1]) : 0, hm ? Number(hm[2]) : 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Minutos até o prazo. Negativo quando já passou; null quando não há prazo. */
export function minutosAtePrazo(ticket, agora = new Date()) {
  const prazo = prazoDoTicket(ticket);
  if (!prazo) return null;
  return Math.round((prazo.getTime() - agora.getTime()) / 60000);
}

/**
 * A fila sem dono (operador 106), da espera mais longa para a mais curta.
 *
 * NÃO é mais um cartão de fila — ela repetia as outras (ver `resumoDasFilas`).
 * Continua existindo como CONSULTA porque o quadro de equipe precisa dela: a
 * fila genérica ocupa uma linha lá, e a idade do mais antigo é o que ela tem de
 * dizer.
 *
 * A idade conta da ABERTURA, e não da última alteração: o que interessa é há
 * quanto tempo o cliente espera alguém assumir.
 */
export function filaSemDono(tickets, agora = new Date()) {
  return (tickets || [])
    .filter(t => donoDoTicket(t) === OPERADOR_SEM_DONO)
    .map(t => ({ ticket: t, dias: diasDesde(t.dataAbertura || t.dataAlteracao, agora) }))
    .sort((a, b) => (b.dias ?? -1) - (a.dias ?? -1));
}

/**
 * Contagem por criticidade, da mais grave para a menos.
 *
 * `descricoes` é o mapa `{ [codigo]: texto }` vindo de orion.criticidade, que o
 * app já lê para os combos. Sem ele, o código cru aparece — melhor um número
 * do que uma linha em branco.
 */
export function porCriticidade(tickets, descricoes = {}) {
  const mapa = {};
  (tickets || []).forEach((t) => {
    const c = Number(t.codigoCriticidade);
    if (!Number.isFinite(c)) return;
    mapa[c] = (mapa[c] || 0) + 1;
  });
  return Object.entries(mapa)
    .map(([codigo, total]) => ({
      codigo: Number(codigo),
      total,
      descricao: descricoes[Number(codigo)] || `Criticidade ${codigo}`,
      // 3 = sistema parado, 4 = afeta setor essencial. São as duas que fazem o
      // gerente largar o que está fazendo.
      grave: Number(codigo) === 3,
      atencao: Number(codigo) === 4,
    }))
    .sort((a, b) => a.codigo - b.codigo);
}

// ── O quadro inteiro ─────────────────────────────────────────────────────────

/**
 * Monta a linha de cada operador do quadro: identidade + estado + carga.
 *
 * `equipe` são os operadores vindos de orion.vw_operador; `periodos`, os
 * períodos em aberto; `comRegistroHoje`, o conjunto de códigos que já têm
 * alguma linha no dia.
 *
 * A fila genérica entra como uma linha a mais, no fim, e não como pessoa: ela
 * precisa aparecer no mesmo quadro (senão fica invisível), mas não tem estado
 * de atividade nem responde por jornada.
 */
export function montarQuadro({ equipe, porOperador, periodos, comRegistroHoje, nomes = {}, agora = new Date() }) {
  const media = cargaMedia(porOperador);
  const abertos = new Map((periodos || []).map(p => [Number(p.operador_codigo), p]));
  const registros = new Set(comRegistroHoje || []);

  const linhas = (equipe || []).map((op) => {
    const codigo = Number(op.codigo);
    const fila = porOperador?.[codigo] || { total: 0, porStatus: {}, tickets: [] };
    const estado = estadoDoOperador(abertos.get(codigo) || null, {
      temRegistroHoje: registros.has(codigo),
      expediente: op.expediente,
      agora,
    });

    return {
      codigo,
      // O mapa vence o nome que veio na equipe: são a mesma fonte (a view), e
      // usar um só caminho evita duas grafias do mesmo nome na mesma tela.
      nome: nomes[codigo] || op.nome || nomeDoOperador(codigo, { nomes }),
      estado,
      total: fila.total,
      porStatus: fila.porStatus,
      tickets: fila.tickets,
      estourados: quantosEstourados(fila.tickets, agora),
      sobrecarregado: estaSobrecarregado(fila.total, media),
      ehFila: false,
    };
  });

  // Ordem: quem precisa de atenção primeiro. Sobrecarga e pausa estourada sobem;
  // no empate, a carga decide. É a ordem em que o gerente lê a tela.
  linhas.sort((a, b) => {
    const peso = l => (l.sobrecarregado ? 2 : 0) + (l.estado.excedeu ? 1 : 0);
    const dif = peso(b) - peso(a);
    return dif !== 0 ? dif : b.total - a.total;
  });

  // ── Quem tem ticket mas não está na equipe ────────────────────────────────
  //
  // Acontece de verdade: em 31/08/2026 havia quatro tickets em "Aguardando
  // validação do cliente" no nome de um operador que hoje é do setor de
  // sistemas, três deles parados há mais de um ano.
  //
  // Deixá-los de fora do quadro criaria um erro silencioso — a soma das cargas
  // não bateria com o total da fila, e ninguém saberia por quê. Eles entram
  // marcados como externos: contam na fila, não contam como equipe de plantão,
  // e ficam visíveis, que é o ponto. Ticket sem alguém de plantão respondendo
  // por ele é exatamente o que o gerente precisa enxergar.
  const naEquipe = new Set((equipe || []).map(o => Number(o.codigo)));
  Object.keys(porOperador || {})
    .map(Number)
    .filter(cod => cod !== OPERADOR_SEM_DONO && !naEquipe.has(cod))
    .sort((a, b) => porOperador[b].total - porOperador[a].total)
    .forEach((cod) => {
      const fila = porOperador[cod];
      linhas.push({
        codigo: cod,
        // O nome vem do mapa completo, e não da equipe: justamente quem está
        // FORA do plantão é que ficava sem nome antes.
        nome: nomeDoOperador(cod, { nomes }),
        estado: {
          chave: 'externo',
          rotulo: 'Fora do plantão',
          tom: 'apagado',
          icone: 'user',
          minutos: null,
          excedeu: false,
        },
        total: fila.total,
        porStatus: fila.porStatus,
        tickets: fila.tickets,
        estourados: quantosEstourados(fila.tickets, agora),
        sobrecarregado: false,
        ehFila: false,
        ehExterno: true,
      });
    });

  const semDono = porOperador?.[OPERADOR_SEM_DONO];
  if (semDono) {
    const espera = filaSemDono(semDono.tickets, agora);
    linhas.push({
      codigo: OPERADOR_SEM_DONO,
      nome: NOME_FILA_GENERICA,
      estado: { chave: 'fila', rotulo: 'Fila genérica', tom: 'alerta', icone: 'inbox', minutos: null, excedeu: false },
      total: semDono.total,
      porStatus: semDono.porStatus,
      tickets: semDono.tickets,
      estourados: quantosEstourados(semDono.tickets, agora),
      sobrecarregado: false,
      ehFila: true,
      esperaDias: espera[0]?.dias ?? null,
    });
  }

  return { linhas, media };
}

/**
 * OS TICKETS POR TRÁS DE UMA MÉTRICA.
 *
 * Todo número do painel tem de poder ser aberto: um "3 vencidos" que não diz
 * QUAIS obriga o gerente a ir procurar no MySuite — e aí o painel virou só um
 * aviso de que existe trabalho, não uma ferramenta para fazê-lo.
 *
 * A ordem de cada lista é a da própria métrica, e não o número do ticket: quem
 * abre "vencidos" quer o mais atrasado no topo, quem abre "parados" quer o mais
 * esquecido. Onde não há urgência natural, o mais recente vem primeiro.
 *
 * Devolve sempre um array (nunca null), para quem exibe não precisar conferir
 * antes de percorrer.
 */
// ── SLA ──────────────────────────────────────────────────────────────────────
//
// O SLA previsto de um ticket é `dataPrevisao` + `horaPrevisao`: o prazo que o
// suporte assumiu com o cliente. É o que ordena esta tela inteira, do mais
// antigo para o mais recente — quem estourou primeiro aparece primeiro.
//
// ─────────────────────────────────────────────────────────────────────────────
// POR QUE A ORDENAÇÃO TEM DOIS NÍVEIS
//
// Porque o campo é ESPARSO. Medido na fila real em 31/08/2026:
//
//   status  1 (aguardando) ...  2 de  5 com previsão
//   status  5 (retorno) ......  5 de 16
//   status 36 (atendimento) ..  6 de 10
//   status 90 (validação) ....  0 de  4
//   status 104 (agendado) ....  3 de  3
//   status 18 (sistemas) .....  8 de 79   ← a maior fila, quase toda sem prazo
//
// No total, cerca de um quinto dos tickets tem SLA. Ordenar só por ele jogaria
// os outros quatro quintos para o fim da lista em ordem arbitrária — e a fila
// que mais precisa de ordem ficaria sem nenhuma.
//
// Por isso: quem TEM SLA vem primeiro, do mais estourado ao mais folgado; quem
// NÃO tem vem depois, do mais antigo ao mais novo. O SLA continua sendo o
// embasamento principal, e o resto não vira um monte sem critério.
//
// A data de referência de quem não tem SLA é a ABERTURA — há quanto tempo o
// cliente espera. Quando ela não vem (a resposta de `/tickets/filtro` não a
// traz), cai na última alteração, que é o que existe.

/** O instante que governa um ticket: o SLA quando existe, `null` quando não. */
export function slaDoTicket(ticket) {
  return prazoDoTicket(ticket);
}

/** A data de referência de quem não tem SLA: abertura, ou a última alteração. */
export function referenciaSemSla(ticket) {
  const bruta = String(ticket?.dataAbertura || ticket?.dataAlteracao || '').trim();
  if (!bruta || bruta.startsWith('0000')) return null;
  const m = bruta.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * A situação do SLA — o que decide a cor da etiqueta.
 *
 *   estourado    · o prazo passou
 *   hoje         · vence ainda hoje
 *   futuro       · vence depois de hoje
 *   sem-sla      · não há prazo combinado
 *   nao-apurado  · a fila está fora da régua (hoje, o Setor de Sistemas)
 *
 * `nao-apurado` vem ANTES de tudo, inclusive de verificar se há prazo: o
 * ticket pode ter data de previsão e ainda assim não ser medido. Oito dos 79
 * do Setor de Sistemas têm previsão, e nenhum deles conta como atraso do
 * suporte.
 */
export function situacaoDoSla(ticket, agora = new Date()) {
  if (!apuraSla(ticket)) return 'nao-apurado';
  const sla = slaDoTicket(ticket);
  if (!sla) return 'sem-sla';
  if (sla.getTime() < agora.getTime()) return 'estourado';
  const fimDoDia = new Date(agora);
  fimDoDia.setHours(23, 59, 59, 999);
  return sla.getTime() <= fimDoDia.getTime() ? 'hoje' : 'futuro';
}

/**
 * O texto do SLA, como ele aparece em cada linha.
 *
 * Mostra a DATA, e não só "há 3 dias": o prazo é um compromisso com hora
 * marcada, e o operador que vai ligar para o cliente precisa saber para quando
 * ficou de ligar. O tempo relativo vem junto porque é ele que diz a urgência
 * sem obrigar a fazer a conta de cabeça.
 */
export function textoDoSla(ticket, agora = new Date()) {
  const pad = n => String(n).padStart(2, '0');
  const sla = slaDoTicket(ticket);

  // Fora da régua: a data de previsão, quando existe, ainda é mostrada — ela
  // informa —, mas SEM "estourou há", que é linguagem de cobrança. O rótulo
  // diz por que aquele ticket não tem um prazo sendo contado contra ninguém.
  if (!apuraSla(ticket)) {
    const idade = referenciaSemSla(ticket);
    const quando = sla ? `prev. ${pad(sla.getDate())}/${pad(sla.getMonth() + 1)} · ` : '';
    if (!sla && idade) {
      const dias = Math.floor((agora.getTime() - idade.getTime()) / 86400000);
      return `SLA não apurado · há ${textoDeDias(dias).replace(/^há /, '')}`;
    }
    return `${quando}SLA não apurado`;
  }

  if (!sla) {
    const ref = referenciaSemSla(ticket);
    if (!ref) return 'sem SLA';
    const dias = Math.floor((agora.getTime() - ref.getTime()) / 86400000);
    return `sem SLA · aberto ${textoDeDias(dias)}`;
  }

  const data = `${pad(sla.getDate())}/${pad(sla.getMonth() + 1)}`;
  const hora = sla.getHours() || sla.getMinutes()
    ? ` ${pad(sla.getHours())}:${pad(sla.getMinutes())}`
    : '';
  const min = Math.round((sla.getTime() - agora.getTime()) / 60000);

  if (min < 0) {
    const atraso = Math.abs(min);
    const quanto = atraso < 60 ? `${atraso} min`
      : atraso < 1440 ? `${Math.floor(atraso / 60)}h`
        : `${Math.floor(atraso / 1440)} dia(s)`;
    return `${data}${hora} · estourou há ${quanto}`;
  }
  if (min < 60) return `${data}${hora} · em ${min} min`;
  if (min < 1440) return `${data}${hora} · em ${Math.floor(min / 60)}h`;
  return `${data}${hora} · em ${Math.floor(min / 1440)} dia(s)`;
}

/**
 * A ordenação da tela: PRIORIDADE primeiro (Alta, Média, Baixa — ver
 * services/prioridade.js) e, dentro da mesma prioridade, o SLA, do mais antigo
 * para o mais recente.
 *
 * Os dois níveis do SLA estão explicados no bloco acima. Dentro de cada um a
 * regra é a mesma — data mais antiga na frente.
 */
export function ordenarPorSla(tickets, agora = new Date()) {
  // Quem está fora da apuração não usa o SLA nem para ordenar: o topo desta
  // lista é, na prática, a resposta a "quem está mais atrasado?", e pôr ali um
  // ticket do Sistemas seria apurar atraso por outro caminho. Eles caem no
  // nível 2 e são ordenados pela idade, como os demais sem SLA.
  const chaveSla = t => (apuraSla(t) ? slaDoTicket(t) : null);

  return [...(tickets || [])].sort((a, b) => {
    // Nível 0: a prioridade do MySuite. Uma Alta com prazo folgado vem antes
    // de uma Baixa estourada — é a regra que o setor pediu.
    const porPrioridade = compararPrioridade(a, b);
    if (porPrioridade) return porPrioridade;

    const sa = chaveSla(a);
    const sb = chaveSla(b);

    // Nível 1: quem tem SLA, entre si, do mais estourado ao mais folgado.
    if (sa && sb) return sa.getTime() - sb.getTime();
    // Ter SLA sempre vem antes de não ter.
    if (sa) return -1;
    if (sb) return 1;

    // Nível 2: sem SLA, o que espera há mais tempo primeiro.
    const ra = referenciaSemSla(a);
    const rb = referenciaSemSla(b);
    if (ra && rb) return ra.getTime() - rb.getTime();
    if (ra) return -1;
    if (rb) return 1;
    return Number(a?.codigo || 0) - Number(b?.codigo || 0);
  });
}

/** Quantos tickets de uma lista estão com o SLA estourado. */
export function quantosEstourados(tickets, agora = new Date()) {
  return (tickets || []).filter(t => situacaoDoSla(t, agora) === 'estourado').length;
}

export function ticketsDaMetrica(chave, { tickets = [], agora = new Date(), valor = null } = {}) {
  const lista = tickets || [];
  // TODA lista da tela sai ordenada por SLA, do mais antigo para o mais
  // recente. A ordenação é aplicada uma vez, aqui, e não em cada bloco: assim
  // não existe caminho pelo qual uma lista chegue à tela fora de ordem.
  const ord = l => ordenarPorSla(l, agora);

  switch (chave) {
    case 'fila':
      return ord(lista);
    case 'emAtendimento':
      return ord(lista.filter(t => Number(t.codigoStatusTicket) === 36));
    case 'semDono':
      return ord(lista.filter(t => donoDoTicket(t) === OPERADOR_SEM_DONO));
    case 'estourados':
      return ord(lista.filter(t => situacaoDoSla(t, agora) === 'estourado'));
    case 'status':
      // `pertenceAFila`, e nao a comparacao crua de status: e ela que
      // aplica a regra da fila de entrada (ver a funcao). O contador da
      // ficha usa a MESMA, e por isso os dois nunca divergem.
      return ord(lista.filter(t => pertenceAFila(t, valor)));
    case 'criticidade':
      return ord(lista.filter(t => Number(t.codigoCriticidade) === Number(valor)));
    // Os tickets que quem está olhando pode puxar agora — a fila livre.
    case 'livres':
      return ord(lista.filter(t => podePuxar(t, valor)));
    // A fila do próprio operador, dentro da visão geral.
    case 'meus':
      return ord(lista.filter(t => donoDoTicket(t) === Number(valor)));
    default:
      return [];
  }
}

/**
 * O resumo de cada FILA do suporte — o bloco que todo operador vê.
 *
 * Uma entrada por status de `STATUS`, na ordem em que ele está declarado, mais
 * a fila sem dono no fim. Cada uma traz o total, quantos estão vencidos e
 * quantos podem ser puxados por quem está olhando.
 *
 * `livres` é o número que muda o comportamento de quem lê: é o que está
 * disponível para pegar agora, sem falar com ninguém.
 */
export function resumoDasFilas(tickets, { agora = new Date(), meuCodigo = null } = {}) {
  const lista = tickets || [];

  // ── POR QUE "SEM DONO" NÃO É UMA FILA ──────────────────────────────────────
  //
  // Era, e estava errado: ela repetia o que já aparecia nas outras. Medido na
  // fila real em 31/08/2026, dos seis tickets sem dono, TRÊS eram exatamente os
  // de "Aguardando solução" e os outros três, os de "Agendado" — dois cartões
  // mostrando os mesmos tickets, e a soma das filas dando mais do que a fila.
  //
  // A causa é conceitual: STATUS e DONO são eixos diferentes. Todo ticket tem
  // um status, e ter ou não ter dono é um atributo que atravessa todos eles.
  // Pôr os dois lado a lado como se fossem irmãos é que produzia a repetição.
  //
  // Agora "sem dono" é o contador `livres` DENTRO de cada fila — e a lista
  // "Livre para pegar", que é a versão acionável da mesma informação.
  return Object.entries(STATUS).map(([codigo, s]) => {
    const cod = Number(codigo);
    const daFila = ordenarPorSla(lista.filter(t => pertenceAFila(t, cod)), agora);
    return {
      codigo: cod,
      chave: s.chave,
      rotulo: s.rotulo,
      sub: s.sub || null,
      curto: s.curto,
      cor: s.cor,
      total: daFila.length,
      // O que conta agora é o SLA estourado, e não só o agendamento vencido: é
      // a régua única da tela.
      estourados: quantosEstourados(daFila, agora),
      livres: daFila.filter(t => podePuxar(t, meuCodigo)).length,
      meus: meuCodigo ? daFila.filter(t => donoDoTicket(t) === Number(meuCodigo)).length : 0,
      tickets: daFila,
    };
  });
}

/**
 * Os quatro números do topo — o que o gerente lê em dois segundos para decidir
 * se precisa olhar o resto.
 */
export function montarTermometro({ tickets, quadro, agora = new Date() }) {
  // "Equipe de pé" conta o PLANTÃO: nem a fila genérica (que não é pessoa) nem
  // quem está fora do suporte (que não deveria estar de plantão). Incluí-los
  // faria o denominador crescer e o time parecer mais desfalcado do que está.
  const linhasDePessoa = (quadro?.linhas || []).filter(l => !l.ehFila && !l.ehExterno);
  const dePe = linhasDePessoa.filter(l => l.estado.chave === 'ativo').length;

  return {
    fila: (tickets || []).length,
    emAtendimento: (tickets || []).filter(t => Number(t.codigoStatusTicket) === 36).length,
    estourados: quantosEstourados(tickets, agora),
    dePe,
    equipe: linhasDePessoa.length,
    ausentes: linhasDePessoa.filter(l => l.estado.chave === 'ausente').length,
    emPausa: linhasDePessoa.filter(l => !['ativo', 'ausente', 'encerrado', 'fora_expediente'].includes(l.estado.chave)).length,
  };
}
